import React from 'react';
import { Box, Button, Link } from '@amzn/awsui-components-core';
import './ManagerHubRatingsGuide.scss';

/** Tampermonkey userscript manager (browser extension the export script installs into). */
const TAMPERMONKEY_URL = 'https://www.tampermonkey.net/';

/**
 * Tampermonkey FAQ entry (Q209) explaining how to enable the "Allow User Scripts" toggle on the
 * extension's Details page — a Chrome requirement before userscripts will run.
 */
const TAMPERMONKEY_ALLOW_USER_SCRIPTS_FAQ_URL = 'https://www.tampermonkey.net/faq.php?q=Q209#Q209';

/**
 * Chrome extensions page (where Developer mode is toggled). Rendered as inline code, not a link:
 * Chrome blocks navigation to `chrome://` URLs from web pages, so a link would be dead — users
 * copy this into the address bar manually.
 */
const CHROME_EXTENSIONS_URL = 'chrome://extensions';

/** Harmony-hosted userscript that adds the one-click "Direct Reports / Entire Team" CSV export. */
const FORTE_EXPORT_SCRIPT_URL = 'https://tamarin.aces.amazon.dev/scripts/forte-team-lp-export/install.user.js';

/** AtoZ Manager Hub team calibrate path; `{alias}` is replaced with the signed-in user's alias. */
const MANAGER_HUB_URL_TEMPLATE = 'https://atoz.amazon.work/manager/team/{alias}/talent-cycle/calibrate';

export interface ManagerHubRatingsGuideProps {
  /** Signed-in user's alias, interpolated into the Run-step Manager Hub URL so the link is per-user. */
  alias: string;
  /**
   * Opens the in-page upload modal for the "Manager Hub Exports" folder. When provided, the
   * Upload step's folder name becomes a button that opens that modal; when omitted it renders as
   * bold text (folders not provisioned yet).
   */
  onOpenUpload?: () => void;
}

/**
 * "How to Export from Manager Hub" guide shown inline under the "Manager Hub Exports" row of the
 * Step 3 Folder Guide. Presentation-only: a bulleted list of bold-labeled points (What it is /
 * One-time setup / Each time you run it), where the setup and run bullets each own a nested
 * numbered list. Rendered flat (no collapsible) to match the reference layout. Sibling of
 * SupplementalContextGuide, but takes an `alias` prop because its run link is user-specific (the
 * page passes `useAuth().Alias` down, keeping this component React-free).
 */
export const ManagerHubRatingsGuide: React.FC<ManagerHubRatingsGuideProps> = ({ alias, onOpenUpload }) => {
  // Build the per-user Manager Hub URL. Encode the alias — it lands in a path segment, so encoding
  // is the correct primitive even though aliases are simple (guards a malformed href for odd values).
  const managerHubUrl = MANAGER_HUB_URL_TEMPLATE.replace('{alias}', encodeURIComponent(alias));

  // The destination folder name: an inline-link button that opens the in-page upload modal when an
  // opener is provided, else plain bold text (folders not provisioned yet).
  const folderLabel = onOpenUpload ? (
    <Button variant="inline-link" onClick={onOpenUpload}>
      Manager Hub Exports
    </Button>
  ) : (
    <Box variant="strong">Manager Hub Exports</Box>
  );

  return (
    // Class-bearing div (not the deprecated `className` prop) so the scoped SCSS can size the list
    // markers and the nested numbered steps.
    <div className="manager-hub-ratings-guide">
      <ul className="manager-hub-ratings-guide__bullets">
        <li>
          <Box variant="strong">What it is:</Box> Exports your team&apos;s ratings, strengths and growth areas, and annual review feedback from
          Manager Hub as a single file. Including the prior quarter lets cards reflect trends over time.
        </li>
        <li>
          <Box variant="strong">One-time setup:</Box>
          <ol className="manager-hub-ratings-guide__steps">
            <li>
              Install the{' '}
              <Link href={TAMPERMONKEY_URL} target="_blank">
                <span className="manager-hub-ratings-guide__link-label">Tampermonkey</span>
              </Link>{' '}
              browser extension.
            </li>
            <li>
              In Chrome, go to <Box variant="awsui-inline-code">{CHROME_EXTENSIONS_URL}</Box> and enable <Box variant="strong">Developer mode</Box>{' '}
              (top-right toggle).
            </li>
            <li>
              Open Tampermonkey&apos;s Details page and turn on the <Box variant="strong">Allow User Scripts</Box> toggle using these{' '}
              <Link href={TAMPERMONKEY_ALLOW_USER_SCRIPTS_FAQ_URL} target="_blank">
                <span className="manager-hub-ratings-guide__link-label">instructions</span>
              </Link>
              .
            </li>
            <li>
              Open the{' '}
              <Link href={FORTE_EXPORT_SCRIPT_URL} target="_blank">
                <span className="manager-hub-ratings-guide__link-label">Script Link</span>
              </Link>{' '}
              → click <Box variant="strong">Install.</Box>
            </li>
          </ol>
        </li>
        <li>
          <Box variant="strong">Each time you run it:</Box>
          <ol className="manager-hub-ratings-guide__steps">
            <li>
              Navigate to{' '}
              <Link href={managerHubUrl} target="_blank">
                <span className="manager-hub-ratings-guide__link-label">Manager Hub</span>
              </Link>
              .
            </li>
            <li>
              At the bottom-right, click <Box variant="strong">Direct Reports</Box> or <Box variant="strong">Entire Team</Box> — the export downloads
              automatically.
            </li>
            <li>Upload the file to the {folderLabel} folder.</li>
          </ol>
        </li>
      </ul>
    </div>
  );
};
